import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { ConsultationStatus } from '@/shared/api/transcriptor/transcriptorApi';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, Icon, Spinner } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { transcriptorModel } from '@/entities/transcriptor';

import { CONTINUABLE_STATUSES, continueRecordingMessages } from '../../config';
import { isConsultationProcessing } from '../../lib';

import styles from './ContinueRecordingButton.module.scss';

type ContinueRecordingButtonProps = {
  patientId: string;
  encounterId: string;
  status?: ConsultationStatus;
  schemaId?: string;
  language?: string;
  className?: string;
};

export const ContinueRecordingButton: FC<ContinueRecordingButtonProps> = (
  props,
) => {
  const { patientId, encounterId, status, schemaId, language, className } =
    props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const isRecording = useAppSelector(
    transcriptorModel.selectors.selectIsRecording,
  );

  const canContinue = Boolean(status && CONTINUABLE_STATUSES.includes(status));
  const isProcessing = isConsultationProcessing(status);

  if (isRecording || isProcessing) {
    return (
      <div className={cn(styles.container, className)}>
        <div className={styles.processing}>
          <Spinner size={20} />
          <span className={cn('p3', styles.processingLabel)}>
            {formatMessage(
              isRecording
                ? continueRecordingMessages.recording
                : continueRecordingMessages.processing,
            )}
          </span>
        </div>
      </div>
    );
  }

  if (!canContinue) {
    return null;
  }

  const handleContinue = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.TranscriptorContinueRecording,
        data: {
          patientID: patientId,
          encounterID: encounterId,
          schemaId,
          language,
        },
      }),
    );
  };

  return (
    <div className={cn(styles.container, className)}>
      <Button
        data-component="continue-recording-button"
        variant="tertiary"
        size="medium"
        icon="microphone"
        onClick={handleContinue}
        className={styles.button}
      >
        {formatMessage(continueRecordingMessages.button)}
      </Button>
      <span className={cn('p3', styles.hint)}>
        <Icon name="info" size={24} className={styles.hintIcon} />
        {formatMessage(continueRecordingMessages.hint)}
      </span>
    </div>
  );
};
